import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { combineLatest, of, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';

import { BrandThemesEnum, ComponentWrapperWidthEnum, SdSpyTargetDirective, ThemesEnum } from '@seb/web-design-system/core';
import { IAccordionImage, SdAccordionGroupImageComponent } from '@seb/web-design-system/accordion-group-image';

import {
  AccordionGroupImageComponentInterface, AccordionGroupImageModuleInterface, accordionImageSkeleton,
  ExtendedIMedia, WC_CONFIG_PROVIDER, WcConfig
} from '../../../models';

import { ChatService, ImageService, TrackingService } from '../../../services';
import { AbstractModuleComponent } from '../abstract-module.component';
import { WrapperComponent } from '../../../components/static/core';
import { LinkMapper, ThemesMapper } from '../../../mappers';

@Component({
  selector: 'wc-accordion-group-image-module',
  templateUrl: './accordion-group-image-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdAccordionGroupImageComponent,
    SdSpyTargetDirective,
    CommonModule
  ]
})
export class AccordionGroupImageModuleComponent extends AbstractModuleComponent<AccordionGroupImageModuleInterface> implements OnInit {
  themesEnum = ThemesEnum;
  slices: IAccordionImage[];
  themeAccordion: ThemesEnum | BrandThemesEnum = ThemesEnum.dark;
  isSmall: boolean;
  componentWidth: ComponentWrapperWidthEnum = ComponentWrapperWidthEnum.default;

  constructor(
    private imageService: ImageService,
    private cdRef: ChangeDetectorRef,
    private chatService: ChatService,
    private trackingService: TrackingService,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig
  ) {
    super();
  }

  override mandatoryComponentFunction = (cmp: AccordionGroupImageComponentInterface): boolean => {
    return (!!cmp.media || !!cmp.mediaContainer) && !!cmp.description;
  };

  override onModuleInit(module: AccordionGroupImageModuleInterface): void {
    const slicesLength = Math.min(module.components.split(' ').length, 5);

    if (this.useLazyLoad) {
      this.slices = (new Array(slicesLength).fill(undefined)).map(
        (_, i) => ({ ...accordionImageSkeleton, uid: `${i}` })
      );
    }

    this.isSmall = module.isSmall === 'true';
    this.componentWidth = module.isFullWidth === 'true' ? ComponentWrapperWidthEnum.fullWidth : ComponentWrapperWidthEnum.default;
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.module$.pipe(
        switchMap((module: AccordionGroupImageModuleInterface) => {
          return combineLatest([
            of(module),
            combineLatest(
              module.componentsObjects.map((cmp) => {
                if (cmp.mediaContainer) {
                  return this.imageService.getMediaFromContainer(cmp.mediaContainer);
                } else {
                  return of(cmp.media);
                }
              })
            )
          ]);
        })
      ).subscribe(([module, images]) => {
        this.themeAccordion = ThemesMapper.mapBrandTheme(module.theme);
        this.slices = module.componentsObjects.slice(0, 5).map((cmp, index) => {
          return this.toAccordionImage(cmp, this.imageService.normalizeMedia(images[index]));
        });
        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  closeSlice(event: IAccordionImage): void {
    this.trackingService.sendEvent('accordionClick', {
      accordionAction: 'Close',
      categoryName: event.headTitle
    });
  }

  openSlice(event: IAccordionImage): void {
    this.trackingService.sendEvent('accordionClick', {
      accordionAction: 'Open',
      categoryName: event.headTitle
    });
  }

  openUrl(event: IAccordionImage): void {
    if (event?.link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    this.trackingService.sendEvent('accordionClick', {
      accordionAction: 'Explore',
      categoryName: event.headTitle
    });
  }

  private toAccordionImage(accordion: AccordionGroupImageComponentInterface, media?: ExtendedIMedia): IAccordionImage {
    return {
      uid: accordion.uid,
      headTitle: accordion.headline,
      title: accordion.subline,
      description: accordion.description,
      media: media ?? this.imageService.normalizeMedia(accordion.media),
      link: LinkMapper.toLink(accordion.urlLocalized, accordion.linkName)
    };
  }
}
